import { loadYandexMaps } from './yandexMaps';

export const parseAddressCoordinate = (value) => {
  if (value === null || value === undefined || String(value).trim() === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

export const completePair = (latitude, longitude) => {
  const lat = parseAddressCoordinate(latitude);
  const lng = parseAddressCoordinate(longitude);
  if (lat === null || lng === null) return null;
  // Валидация географических диапазонов: широта [-90..90], долгота [-180..180]
  if (lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180) {
    return { lat, lng };
  }
  return null;
};

export const readStoredAddressCoordinates = (data = {}) => {
  const dadata = completePair(
    data.dadata_latitude ?? data.dadata_lat ?? data.geo_lat,
    data.dadata_longitude ?? data.dadata_lng ?? data.geo_lon
  );
  const yandex = completePair(
    data.yandex_latitude ?? data.yandex_lat,
    data.yandex_longitude ?? data.yandex_lng
  );
  const osm = completePair(
    data.osm_latitude ?? data.osm_lat,
    data.osm_longitude ?? data.osm_lng
  );
  const canonical = completePair(
    data.latitude ?? data.lat,
    data.longitude ?? data.lng
  );
  // Приоритет сохранённых координат: сначала DaData (самые точные для РФ), затем Яндекс, канонические, затем OSM
  const preferred = dadata || yandex || canonical || osm;

  return {
    latitude: preferred?.lat ?? null,
    longitude: preferred?.lng ?? null,
    dadataLatitude: dadata?.lat ?? null,
    dadataLongitude: dadata?.lng ?? null,
    yandexLatitude: yandex?.lat ?? null,
    yandexLongitude: yandex?.lng ?? null,
    osmLatitude: osm?.lat ?? null,
    osmLongitude: osm?.lng ?? null
  };
};

export const geocodeAddressWithYandex = async (apiKey, addressText) => {
  if (!apiKey || !String(addressText || '').trim()) return null;
  const ymaps = await loadYandexMaps(apiKey);
  const result = await ymaps.geocode(String(addressText).trim(), { results: 1 });
  const firstGeoObject = result?.geoObjects?.get?.(0);
  const coordinates = firstGeoObject?.geometry?.getCoordinates?.();
  if (!Array.isArray(coordinates) || coordinates.length !== 2) return null;
  // В Яндекс.Картах JS API v2 getCoordinates возвращает [latitude, longitude]
  return completePair(coordinates[0], coordinates[1]);
};

/**
 * Резервный геокодер через OpenStreetMap (Nominatim)
 * Вызывается, если DaData и Яндекс недоступны или не нашли точку.
 */
export const geocodeAddressWithNominatim = async (addressText) => {
  const cleanAddress = String(addressText || '').trim();
  if (!cleanAddress) return null;

  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 6000);

    const url = `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(cleanAddress)}&limit=1&addressdetails=1`;
    const response = await fetch(url, {
      signal: controller.signal,
      headers: {
        Accept: 'application/json'
      }
    });
    clearTimeout(timeoutId);

    if (!response.ok) return null;
    const items = await response.json();
    if (!Array.isArray(items) || items.length === 0) return null;

    const first = items[0];
    // Nominatim возвращает { lat: "57.62", lon: "39.83" } (строки)
    return completePair(first.lat, first.lon);
  } catch (error) {
    console.warn('Не удалось уточнить координаты через Nominatim (OSM):', error);
    return null;
  }
};

/**
 * Трёхуровневое определение координат:
 * 1. DaData / уже имеющиеся в подсказке координаты (не тратит лимит Яндекса)
 * 2. Яндекс Геокодер (если ключа нет или запрос упал — идём на шаг 3)
 * 3. Nominatim OpenStreetMap (бесплатный резервный геокодер)
 */
export const resolvePreferredAddressCoordinates = async ({ apiKey, addressText, data = {} }) => {
  const stored = readStoredAddressCoordinates(data);

  // 1-й уровень: если валидные координаты уже есть от DaData или сохранены в заказе
  if (stored.latitude !== null && stored.longitude !== null) {
    return {
      latitude: stored.latitude,
      longitude: stored.longitude,
      dadataLatitude: stored.dadataLatitude,
      dadataLongitude: stored.dadataLongitude,
      yandexLatitude: stored.yandexLatitude,
      yandexLongitude: stored.yandexLongitude,
      osmLatitude: stored.osmLatitude,
      osmLongitude: stored.osmLongitude
    };
  }

  // 2-й уровень: пробуем геокодирование через Яндекс
  let resolvedYandex = null;
  if (apiKey && String(addressText || '').trim()) {
    try {
      resolvedYandex = await geocodeAddressWithYandex(apiKey, addressText);
    } catch (error) {
      console.warn('Не удалось уточнить координаты через Яндекс:', error);
    }
  }

  if (resolvedYandex) {
    return {
      latitude: resolvedYandex.lat,
      longitude: resolvedYandex.lng,
      dadataLatitude: stored.dadataLatitude,
      dadataLongitude: stored.dadataLongitude,
      yandexLatitude: resolvedYandex.lat,
      yandexLongitude: resolvedYandex.lng,
      osmLatitude: stored.osmLatitude,
      osmLongitude: stored.osmLongitude
    };
  }

  // 3-й уровень: резерв через OpenStreetMap (Nominatim)
  let resolvedOsm = null;
  if (String(addressText || '').trim()) {
    try {
      resolvedOsm = await geocodeAddressWithNominatim(addressText);
    } catch (error) {
      console.warn('Не удалось уточнить координаты через Nominatim:', error);
    }
  }

  return {
    latitude: resolvedOsm?.lat ?? stored.latitude,
    longitude: resolvedOsm?.lng ?? stored.longitude,
    dadataLatitude: stored.dadataLatitude,
    dadataLongitude: stored.dadataLongitude,
    yandexLatitude: stored.yandexLatitude,
    yandexLongitude: stored.yandexLongitude,
    osmLatitude: resolvedOsm?.lat ?? stored.osmLatitude,
    osmLongitude: resolvedOsm?.lng ?? stored.osmLongitude
  };
};

export const mergeAddressSuggestionCoordinates = (suggestion, coordinates) => ({
  ...suggestion,
  data: {
    ...(suggestion?.data || {}),
    latitude: coordinates.latitude,
    longitude: coordinates.longitude,
    dadata_latitude: coordinates.dadataLatitude,
    dadata_longitude: coordinates.dadataLongitude,
    yandex_latitude: coordinates.yandexLatitude,
    yandex_longitude: coordinates.yandexLongitude,
    osm_latitude: coordinates.osmLatitude ?? null,
    osm_longitude: coordinates.osmLongitude ?? null
  }
});
